import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { selectUserCurrency, useCancelRequest, useSportUrlParams } from '@mwl/core-lib';
import { GroupedLines } from '@/components/GroupedLines/GroupedLines';
import { Loader } from '@/components/Loader/Loader';
import { useTypedSelector } from '@/hooks';
import { initialState } from './LinesLoadable.data';
import type { LinesLoadableProps, LinesLoadableState, ListType } from './LinesLoadable.types';
import styles from './LinesLoadable.module.scss';
const BaseLinesLoadable: FC<LinesLoadableProps> = ({
  loadLines,
  onLoadFinished,
  pageSize,
  onEmptyLines,
  emptyMessage,
  variant
}) => {
  const pageOffset = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [state, setState] = useState<LinesLoadableState>(initialState);
  const {
    stage
  } = useSportUrlParams();
  const currency = useTypedSelector(selectUserCurrency);
  const cancelRequest = useCancelRequest();
  const actualVariant = useMemo(() => {
    if (variant) {
      return variant;
    }
    if (stage === 'OUTRIGHT') {
      return 'OUTRIGHT';
    }
    return 'TRADITIONAL';
  }, [stage, variant]);
  const fetchLines = useCallback(async (offset: number, isHasPrevPage?: boolean) => {
    setIsLoading(true);
    const lines = await loadLines({
      pageOffset: offset
    });
    if (cancelRequest.current) {
      return;
    }
    const hasMore = lines.length && pageSize ? pageSize <= lines.length : false;
    setState(prev => {
      const result = isHasPrevPage ? [...prev.list, ...lines] : [...lines];
      return {
        list: (result as ListType),
        hasMore
      };
    });
    setIsLoading(false);
    if (!hasMore) {
      onLoadFinished?.();
    }
  }, [loadLines, cancelRequest, pageSize, onLoadFinished]);
  const loadNext = useCallback(() => {
    pageOffset.current += 1;
    fetchLines(pageOffset.current, true);
  }, [fetchLines]);
  useEffect(() => {
    if (currency) {
      setState(initialState);
      pageOffset.current = 0;
      fetchLines(0);
    }
  }, [fetchLines, currency]);
  useEffect(() => {
    if (!isLoading && !state.list.length) {
      onEmptyLines?.(true);
    } else {
      onEmptyLines?.(false);
    }
  }, [isLoading, onEmptyLines, state.list.length]);
  const loader = <div className={styles.loaderWrapper}>
      <Loader />
    </div>;
  const {
    hasMore,
    list
  } = state;
  return <InfiniteScroll className={cn(styles.infinityScroll, {
    [styles.outrightLines]: stage === 'OUTRIGHT'
  })} next={loadNext} dataLength={list.length} hasChildren hasMore={hasMore} loader={loader} data-sentry-element="InfiniteScroll" data-sentry-component="BaseLinesLoadable" data-sentry-source-file="LinesLoadable.tsx">
      {isLoading && pageOffset.current === 0 && loader}
      {!!list.length && <GroupedLines className={styles.group} lines={list} variant={actualVariant} />}
      {!isLoading && !list.length && emptyMessage}
    </InfiniteScroll>;
};
export const LinesLoadable = memo(BaseLinesLoadable);
LinesLoadable.displayName = 'LinesLoadable';
export * from './LinesLoadable.types';